/**
 * Leasing / Mietkauf für Firmen (Kevin 31.10.2026): echte Raten vom Finanzierungspartner (GET /public/leasing/rates),
 * Auswahl Laufzeit + Art, Anfrage direkt beim Partner (POST /public/leasing/request). Anbieter wird NICHT genannt.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { liveEvent } from "@/lib/liveEvent";
import { adsConversion } from "@/lib/ads";

const eur2 = (n) => `${Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const inp = "w-full rounded-lg border border-hair bg-white px-3 py-2.5 text-[16px] md:text-sm text-ink outline-none focus:border-brand-yellow";
const lab = "block text-[11px] font-semibold text-ink-soft mb-1";

export default function LeasingPanel({ price, productName, fallback }) {
  const [data, setData] = useState(null);
  const [type, setType] = useState("leasing");
  const [dur, setDur] = useState(null);
  const [form, setForm] = useState(false);
  const [f, setF] = useState({ customer_type: "company", company: "", first_name: "", last_name: "", email: "", phone: "", street: "", house_no: "", zip: "", city: "", country: "DE", message: "", privacy: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/public/leasing/rates", { params: { gross: price } })
      .then((r) => { if (alive) setData(r.data?.available ? r.data : fallback); })
      .catch(() => { if (alive) setData(fallback); });
    return () => { alive = false; };
  }, [price]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-ink-muted" /></div>;
  const rows = data.rows || [];
  const key = type === "leasing" ? "leasing" : "mietkauf";
  const sel = rows.find((r) => r.m === dur) || rows[rows.length - 1];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data: res } = await api.post("/public/leasing/request", { ...f, product: productName, gross: price, duration: sel.m, calc_type: type, path: window.location.pathname });
      adsConversion("anfrage");
      liveEvent("Leasing-Anfrage gesendet", `${productName} · ${type === "leasing" ? "Leasing" : "Mietkauf"} ${sel.m} Monate`);
      setDone(res);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Das hat nicht geklappt. Bitte versuchen Sie es erneut oder schreiben Sie uns.");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="rounded-xl bg-[#f1fbf5] border border-[#1f8a4c]/30 p-4 text-sm" data-testid="leasing-done">
      <div className="flex items-center gap-2 font-semibold text-[#1f8a4c]"><CheckCircle2 className="w-5 h-5" /> Anfrage gesendet</div>
      <p className="mt-2 text-ink-soft leading-relaxed">Vielen Dank! Sie erhalten das Angebot unseres Finanzierungspartners in Kürze per E-Mail und können es dort prüfen und digital unterschreiben. Eine Bestätigung ist schon unterwegs.</p>
    </div>
  );

  return (
    <div data-testid="leasing-panel">
      <div className="inline-flex rounded-full border border-hair p-0.5 text-[13px] font-semibold">
        {[["leasing", "Leasing"], ["rent_to_own", "Mietkauf"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setType(k)} className={`px-3.5 py-1.5 rounded-full ${type === k ? "bg-ink text-white" : "text-ink-soft"}`}>{l}</button>
        ))}
      </div>
      <p className="mt-2 text-[12px] text-ink-muted">{type === "leasing" ? "Feste Monatsrate, Sie nutzen die Fotobox vom ersten Tag an." : "Feste Monatsrate, nach der letzten Rate gehört die Fotobox Ihnen."}</p>
      <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Laufzeit">
        {rows.map((r) => (
          <button key={r.m} type="button" role="radio" aria-checked={sel?.m === r.m} onClick={() => setDur(r.m)}
            className={`rounded-xl border px-2 py-2 text-center ${sel?.m === r.m ? "border-brand-yellow bg-brand-yellow/10" : "border-hair bg-white"}`}>
            <span className="block text-[11px] text-ink-muted">{r.m} Monate</span>
            <span className="block text-sm font-bold text-ink">{eur2(r[key])}</span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-ink-muted">Monatsraten netto zzgl. MwSt. auf Basis von {eur2(data.net)} netto. Unverbindlich, nach Prüfung durch unseren Finanzierungspartner. Voraussetzung ist eine Sachversicherung für das Gerät.</p>

      {!form ? (
        <button type="button" onClick={() => { setForm(true); liveEvent("Leasing-Formular geöffnet", `${productName} · ${sel.m} Monate`); }} className="btn-accent mt-4 !py-3 inline-flex" data-testid="leasing-open-form">
          Angebot für {sel.m} Monate anfordern
        </button>
      ) : (
        <form onSubmit={submit} className="mt-4 grid gap-3" data-testid="leasing-form" aria-label="Finanzierungsanfrage">
          <div className="flex gap-2 text-[13px]">
            {[["company", "Firma (GmbH, UG, KG …)"], ["freelancer", "Selbstständig / Einzelunternehmen"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, customer_type: k })} className={`flex-1 rounded-lg border px-2 py-2 ${f.customer_type === k ? "border-brand-yellow bg-brand-yellow/10 font-semibold" : "border-hair"}`}>{l}</button>
            ))}
          </div>
          <label><span className={lab}>Firmenname inkl. Rechtsform *</span><input className={inp} required value={f.company} onChange={set("company")} autoComplete="organization" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label><span className={lab}>Vorname *</span><input className={inp} required value={f.first_name} onChange={set("first_name")} autoComplete="given-name" /></label>
            <label><span className={lab}>Nachname *</span><input className={inp} required value={f.last_name} onChange={set("last_name")} autoComplete="family-name" /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label><span className={lab}>E-Mail *</span><input type="email" className={inp} required value={f.email} onChange={set("email")} autoComplete="email" /></label>
            <label><span className={lab}>Telefon</span><input type="tel" className={inp} value={f.phone} onChange={set("phone")} autoComplete="tel" /></label>
          </div>
          <div className="grid grid-cols-[minmax(0,1fr)_90px] gap-3">
            <label><span className={lab}>Straße *</span><input className={inp} required value={f.street} onChange={set("street")} autoComplete="address-line1" /></label>
            <label><span className={lab}>Nr.</span><input className={inp} value={f.house_no} onChange={set("house_no")} /></label>
          </div>
          <div className="grid grid-cols-[110px_minmax(0,1fr)_80px] gap-3">
            <label><span className={lab}>PLZ *</span><input className={inp} required inputMode="numeric" value={f.zip} onChange={set("zip")} autoComplete="postal-code" /></label>
            <label><span className={lab}>Ort *</span><input className={inp} required value={f.city} onChange={set("city")} autoComplete="address-level2" /></label>
            <label><span className={lab}>Land</span>
              <select className={inp} value={f.country} onChange={set("country")}>{["DE", "AT"].map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </label>
          </div>
          <label><span className={lab}>Nachricht (freiwillig)</span><textarea rows={2} className={inp} value={f.message} onChange={set("message")} /></label>
          <label className="flex gap-2.5 items-start text-[12px] text-ink-soft leading-relaxed">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#E8A83D]" required checked={f.privacy} onChange={set("privacy")} />
            <span>Ich bin damit einverstanden, dass meine Angaben zur Erstellung eines Finanzierungsangebots und zur Bonitätsprüfung an unseren Finanzierungspartner übermittelt werden. Details in der <Link to="/datenschutz" target="_blank" className="underline">Datenschutzerklärung</Link>.</span>
          </label>
          {err && <p className="text-sm text-red-600">{err}</p>}
          <button type="submit" disabled={busy} className="btn-accent !py-3 justify-center" data-testid="leasing-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Finanzierungsangebot anfordern
          </button>
          <p className="text-[11px] text-ink-muted text-center">Kostenlos und unverbindlich. Nur für Unternehmen und Selbstständige.</p>
        </form>
      )}
    </div>
  );
}
